import { cloneElement, useCallback, useRef, type ReactElement, type ReactNode } from "react";
import { useControllableOpen, useDismiss, useFloating, type OpenControl } from "./hooks";
import { cx } from "./cx";

export function Chevron() {
  return <span className="ax-chevron" aria-hidden />;
}

export interface PopoverProps extends OpenControl {
  trigger: ReactElement;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: "start" | "end";
  width?: number | string;
  block?: boolean;
  role?: string;
  haspopup?: "dialog" | "menu" | "listbox";
  className?: string;
}

/** 浮层基础：触发元素加挂在其下方的面板；面板相对视口定位，不被滚动容器裁掉（useFloating）。 */
export function Popover({ trigger, children, align, width, block, role = "dialog", haspopup = "dialog", className, ...ctl }: PopoverProps) {
  const [open, setOpen] = useControllableOpen(ctl);
  const ref = useRef<HTMLSpanElement>(null);
  const close = useCallback(() => setOpen(false), [setOpen]);
  useDismiss(ref, open, close);
  const floating = useFloating(ref, open, align, close);
  const t = trigger as ReactElement<Record<string, unknown>>;
  const el = cloneElement(t, {
    onClick: (e: React.MouseEvent) => { (t.props.onClick as ((e: React.MouseEvent) => void) | undefined)?.(e); setOpen(!open); },
    "aria-expanded": open,
    "aria-haspopup": haspopup,
  });
  return (
    <span className={cx("ax-popover", block && "block", className)} ref={ref}>
      {el}
      {open && (
        <div className={cx("ax-popover-panel", align === "end" && "end")} style={{ ...floating, ...(width ? { width } : {}) }} role={role}>
          {typeof children === "function" ? children(close) : children}
        </div>
      )}
    </span>
  );
}
